import React, { useEffect, useState } from 'react';
import EmptyState from '../util/EmptyState';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import TableSortLabel from '@mui/material/TableSortLabel';
import { t } from 'i18next';
import Checkbox from '@mui/material/Checkbox';
import Paginations from '../util/Paginations';
import DeleteArrayAll from '../modal/DeleteArrayAll';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';
import { MODULES } from './adminConstants';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: 'var(--text-color)',
  padding: '4px 10px',
  backgroundColor: 'var(--background-color)',
  borderBottom: 'var(--border-input)',
}));

const StyledTableRow = styled(TableCell)(({ theme }) => ({
  color: 'var(--text-color)',
  padding: '0 7px',
  backgroundColor: 'var(--btn-user-form)',
  borderBottom: 'var(--border-input)',
  fontSize: 13
}));

const StyledTableCheck = styled(TableCell)(({ theme }) => ({
  color: 'var(--text-color)',
  padding: '0 0 0 8px',
  backgroundColor: 'var(--btn-user-form)',
  borderBottom: 'var(--border-input)',
  fontSize: 14
}));

const StyledTableSort = styled(TableSortLabel)(({ theme }) => ({
  fontSize: 14,
}));

interface ReportsTableProps {
  companyRow: any,
  selected: any,
  setSelected: any,
  refreshCompany: any
}

const TariffTable: React.FC<ReportsTableProps> = ({
  companyRow,
  selected,
  setSelected,
  refreshCompany }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [orderBy, setOrderBy] = useState('first_name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [openDel, setOpenDel] = useState(false);
  const [activeEdit, setActiveEdit] = useState<{ id: number, field: string } | null>(null);

  const paginatedUsers = companyRow
    .sort((a: any, b: any) => {
      const compareValueA = order === 'asc' ? a.name : b.name;
      const compareValueB = order === 'asc' ? b.name : a.name;
      return compareValueA.localeCompare(compareValueB);
    })
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = companyRow.map((n: any) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleShowDeleteAll = () => {
    setOpenDel(true);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  useEffect(() => {
  }, [companyRow])

  const updateTariffField = async (id: number, field: string, value: any) => {
    try {
      const numericFields = [
        'monthly_price',
        'yearly_price',
        'trial_days',
        'max_users',
        'max_storage',
        'max_projects'
      ];
      const payload = numericFields.includes(field) ? { [field]: Number(value) } : { [field]: value };
      await axios.put(`/tariff/${id}`, payload);
      refreshCompany();
    } catch (error) {
      console.error("Error updating field:", error);
    }
  };

  return (
    <>
      {Array.isArray(companyRow) && companyRow.length > 0 ? (
        <div className='mb-[10px] text-center'>
          <div
            className={`flex items-center ${selected.length > 0 ? 'py-[5px] px-[10px]' : ''}`}
            style={{
              backgroundColor: selected.length > 0 ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
            }}>
            {selected.length > 0 ? (
              <div
                className='flex justify-between w-full'>
                {selected.length} {t('Selected')}
              </div>
            ) : (<></>)}
            {selected.length > 0 && (
              <div className='flex gap-[20px] items-center'>
                <FontAwesomeIcon
                  icon={faTrashCan}
                  className='text-[16px] cursor-pointer opacity-50 pr-[10px] text-[var(--text-color)] hover:opacity-70'
                  onClick={() => handleShowDeleteAll()}
                />
              </div>
            )}
          </div>
          <TableContainer
            component={Paper}
            style={{ boxShadow: 'none', backgroundColor: 'inherit', borderBottom: 'var(--border-input)' }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={selected.length > 0 && selected.length < companyRow.length}
                      checked={companyRow.length > 0 && selected.length === companyRow.length}
                      onChange={handleSelectAllClick}
                      inputProps={{
                        'aria-label': 'select all equipment',
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <StyledTableSort
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleRequestSort('name')}>
                      {t('Name')}
                    </StyledTableSort>
                  </StyledTableCell>
                  <StyledTableCell>{t('Description')}</StyledTableCell>
                  <StyledTableCell>{t('MonthlyPrice')}</StyledTableCell>
                  <StyledTableCell>{t('Yearly Price')}</StyledTableCell>
                  <StyledTableCell>{t('Trial Days')}</StyledTableCell>
                  <StyledTableCell>{t('Has Branding')}</StyledTableCell>
                  <StyledTableCell>{t('Priority Support')}</StyledTableCell>
                  <StyledTableCell>{t('Max Users')}</StyledTableCell>
                  <StyledTableCell>{t('Max Storage')}</StyledTableCell>
                  <StyledTableCell>{t('Max Projects')}</StyledTableCell>
                  <StyledTableCell>{t('Has Api Access')}</StyledTableCell>
                  <StyledTableCell>{t('Has Integrations')}</StyledTableCell>
                  <StyledTableCell>{t('Active')}</StyledTableCell>
                  <StyledTableCell>Modules</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((company: any, index: any) => {
                  return (
                    <TableRow
                      key={company.dateId || index}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <StyledTableCheck padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={selected.indexOf(company.id) !== -1}
                          onChange={(event: any) => handleClick(event, company.id)}
                        />
                      </StyledTableCheck>
                      <StyledTableRow className="relative group">
                        <input
                          className={`bg-transparent outline-none px-1 py-0.5 rounded border ${activeEdit?.id === company.id && activeEdit?.field === 'name'
                            ? 'border-blue-500'
                            : 'border-transparent'
                            } focus:border-blue-500 transition-colors`}
                          title="Tariff name"
                          placeholder="Name"
                          defaultValue={company.name}
                          size={company.name.length || 1}
                          onFocus={(e) => {
                            e.target.select();
                            setActiveEdit({ id: company.id, field: 'name' });
                          }}
                          onBlur={(e) => {
                            updateTariffField(company.id, 'name', e.target.value);
                            setActiveEdit(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                        <EditIcon
                          fontSize="small"
                          className={`absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 ${activeEdit?.id === company.id && activeEdit?.field === 'name'
                            ? 'opacity-50'
                            : 'opacity-0 group-hover:opacity-70'
                            } transition-opacity`}
                        />
                      </StyledTableRow>
                      <StyledTableRow className="relative group">
                        <input
                          className={`bg-transparent outline-none px-1 py-0.5 rounded border ${activeEdit?.id === company.id && activeEdit?.field === 'description'
                            ? 'border-blue-500'
                            : 'border-transparent'
                            } focus:border-blue-500 transition-colors`}
                          title="Description"
                          placeholder="Description"
                          defaultValue={company.description}
                          size={company.description.length || 1}
                          onFocus={(e) => {
                            e.target.select();
                            setActiveEdit({ id: company.id, field: 'description' });
                          }}
                          onBlur={(e) => {
                            updateTariffField(company.id, 'description', e.target.value);
                            setActiveEdit(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                        <EditIcon
                          fontSize="small"
                          className={`absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 ${activeEdit?.id === company.id && activeEdit?.field === 'description'
                            ? 'opacity-50'
                            : 'opacity-0 group-hover:opacity-70'
                            } transition-opacity`}
                        />
                      </StyledTableRow>
                      <StyledTableRow className="relative group">
                        <input
                          className={`bg-transparent outline-none px-1 py-0.5 rounded border ${activeEdit?.id === company.id && activeEdit?.field === 'monthly_price'
                            ? 'border-blue-500'
                            : 'border-transparent'
                            } focus:border-blue-500 transition-colors`}
                          defaultValue={company.monthly_price}
                          size={String(company.monthly_price).length || 1}
                          onFocus={(e) => {
                            e.target.select();
                            setActiveEdit({ id: company.id, field: 'monthly_price' });
                          }}
                          onChange={(e: any) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, '');
                            if ((value.match(/\./g) || []).length > 1) {
                              value = value.slice(0, -1);
                            }
                            e.target.value = value;
                          }}
                          onBlur={(e) => {
                            const numValue = Number(e.target.value) || 0;
                            updateTariffField(company.id, 'monthly_price', numValue);
                            setActiveEdit(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                        <EditIcon
                          fontSize="small"
                          className={`absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 ${activeEdit?.id === company.id && activeEdit?.field === 'monthly_price'
                            ? 'opacity-50'
                            : 'opacity-0 group-hover:opacity-70'
                            } transition-opacity`}
                        />
                      </StyledTableRow>
                      <StyledTableRow className="relative group">
                        <input
                          className={`bg-transparent outline-none px-1 py-0.5 rounded border ${activeEdit?.id === company.id && activeEdit?.field === 'yearly_price'
                            ? 'border-blue-500'
                            : 'border-transparent'
                            } focus:border-blue-500 transition-colors`}
                          defaultValue={company.yearly_price}
                          size={String(company.yearly_price).length || 1}
                          onFocus={(e) => {
                            e.target.select();
                            setActiveEdit({ id: company.id, field: 'yearly_price' });
                          }}
                          onChange={(e: any) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, '');
                            if ((value.match(/\./g) || []).length > 1) {
                              value = value.slice(0, -1);
                            }
                            e.target.value = value;
                          }}
                          onBlur={(e) => {
                            const numValue = Number(e.target.value) || 0;
                            updateTariffField(company.id, 'yearly_price', numValue);
                            setActiveEdit(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                        <EditIcon
                          fontSize="small"
                          className={`absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 ${activeEdit?.id === company.id && activeEdit?.field === 'yearly_price'
                            ? 'opacity-50'
                            : 'opacity-0 group-hover:opacity-70'
                            } transition-opacity`}
                        />
                      </StyledTableRow>
                      <StyledTableRow className="relative group">
                        <input
                          className={`bg-transparent outline-none px-1 py-0.5 rounded border ${activeEdit?.id === company.id && activeEdit?.field === 'trial_days'
                            ? 'border-blue-500'
                            : 'border-transparent'
                            } focus:border-blue-500 transition-colors`}
                          defaultValue={company.trial_days}
                          size={String(company.trial_days).length || 1}
                          onFocus={(e) => {
                            e.target.select();
                            setActiveEdit({ id: company.id, field: 'trial_days' });
                          }}
                          onChange={(e: any) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, '');
                            if ((value.match(/\./g) || []).length > 1) {
                              value = value.slice(0, -1);
                            }
                            e.target.value = value;
                          }}
                          onBlur={(e) => {
                            const numValue = Number(e.target.value) || 0;
                            updateTariffField(company.id, 'trial_days', numValue);
                            setActiveEdit(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                        <EditIcon
                          fontSize="small"
                          className={`absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 ${activeEdit?.id === company.id && activeEdit?.field === 'trial_days'
                            ? 'opacity-50'
                            : 'opacity-0 group-hover:opacity-70'
                            } transition-opacity`}
                        />
                      </StyledTableRow>
                      <StyledTableRow>
                        <Checkbox
                          checked={company.has_branding}
                          onChange={(e) =>
                            updateTariffField(company.id, "has_branding", e.target.checked)
                          }
                        />
                      </StyledTableRow>
                      <StyledTableRow>
                        <Checkbox
                          checked={company.priority_support}
                          onChange={(e) =>
                            updateTariffField(company.id, "priority_support", e.target.checked)
                          }
                        />
                      </StyledTableRow>
                      <StyledTableRow className="relative group">
                        <input
                          className={`bg-transparent outline-none px-1 py-0.5 rounded border ${activeEdit?.id === company.id && activeEdit?.field === 'max_users'
                            ? 'border-blue-500'
                            : 'border-transparent'
                            } focus:border-blue-500 transition-colors`}
                          defaultValue={company.max_users}
                          size={String(company.max_users).length || 1}
                          onFocus={(e) => {
                            e.target.select();
                            setActiveEdit({ id: company.id, field: 'max_users' });
                          }}
                          onChange={(e: any) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, '');
                            if ((value.match(/\./g) || []).length > 1) {
                              value = value.slice(0, -1);
                            }
                            e.target.value = value;
                          }}
                          onBlur={(e) => {
                            const numValue = Number(e.target.value) || 0;
                            updateTariffField(company.id, 'max_users', numValue);
                            setActiveEdit(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                        <EditIcon
                          fontSize="small"
                          className={`absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 ${activeEdit?.id === company.id && activeEdit?.field === 'max_users'
                            ? 'opacity-50'
                            : 'opacity-0 group-hover:opacity-70'
                            } transition-opacity`}
                        />
                      </StyledTableRow>
                      <StyledTableRow className="relative group">
                        <input
                          className={`bg-transparent outline-none px-1 py-0.5 rounded border ${activeEdit?.id === company.id && activeEdit?.field === 'max_storage'
                            ? 'border-blue-500'
                            : 'border-transparent'
                            } focus:border-blue-500 transition-colors`}
                          defaultValue={company.max_storage}
                          size={String(company.max_storage).length || 1}
                          onFocus={(e) => {
                            e.target.select();
                            setActiveEdit({ id: company.id, field: 'max_storage' });
                          }}
                          onChange={(e: any) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, '');
                            if ((value.match(/\./g) || []).length > 1) {
                              value = value.slice(0, -1);
                            }
                            e.target.value = value;
                          }}
                          onBlur={(e) => {
                            const numValue = Number(e.target.value) || 0;
                            updateTariffField(company.id, 'max_storage', numValue);
                            setActiveEdit(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                        <EditIcon
                          fontSize="small"
                          className={`absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 ${activeEdit?.id === company.id && activeEdit?.field === 'max_storage'
                            ? 'opacity-50'
                            : 'opacity-0 group-hover:opacity-70'
                            } transition-opacity`}
                        />
                      </StyledTableRow>
                      <StyledTableRow className="relative group">
                        <input
                          className={`bg-transparent outline-none px-1 py-0.5 rounded border ${activeEdit?.id === company.id && activeEdit?.field === 'max_projects'
                            ? 'border-blue-500'
                            : 'border-transparent'
                            } focus:border-blue-500 transition-colors`}
                          defaultValue={company.max_projects}
                          size={String(company.max_projects).length || 1}
                          onFocus={(e) => {
                            e.target.select();
                            setActiveEdit({ id: company.id, field: 'max_projects' });
                          }}
                          onChange={(e: any) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9.]/g, '');
                            if ((value.match(/\./g) || []).length > 1) {
                              value = value.slice(0, -1);
                            }
                            e.target.value = value;
                          }}
                          onBlur={(e) => {
                            const numValue = Number(e.target.value) || 0;
                            updateTariffField(company.id, 'max_projects', numValue);
                            setActiveEdit(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                        <EditIcon
                          fontSize="small"
                          className={`absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 ${activeEdit?.id === company.id && activeEdit?.field === 'max_projects'
                            ? 'opacity-50'
                            : 'opacity-0 group-hover:opacity-70'
                            } transition-opacity`}
                        />
                      </StyledTableRow>
                      <StyledTableRow>
                        <Checkbox
                          checked={company.has_api_access}
                          onChange={(e) =>
                            updateTariffField(company.id, "has_api_access", e.target.checked)
                          }
                        />
                      </StyledTableRow>
                      <StyledTableRow>
                        <Checkbox
                          checked={company.has_integrations}
                          onChange={(e) =>
                            updateTariffField(company.id, "has_integrations", e.target.checked)
                          }
                        />
                      </StyledTableRow>
                      <StyledTableRow>
                        <Checkbox
                          checked={company.active}
                          onChange={(e) =>
                            updateTariffField(company.id, "active", e.target.checked)
                          }
                        />
                      </StyledTableRow>
                      {/* Modules */}
                      <StyledTableRow>
                        <div className="flex flex-wrap gap-[2px] max-w-[200px]">
                          {MODULES.map(mod => {
                            const active = company.allowed_modules?.includes(mod) ?? false;
                            return (
                              <span
                                key={mod}
                                title={mod}
                                onClick={() => {
                                  const next = active
                                    ? (company.allowed_modules ?? []).filter((m: string) => m !== mod)
                                    : [...(company.allowed_modules ?? []), mod];
                                  updateTariffField(company.id, 'allowed_modules', next);
                                }}
                                className={`inline-block px-[5px] py-[1px] rounded-[10px] text-[9px] font-medium cursor-pointer capitalize ${
                                  active
                                    ? 'bg-[var(--color-info-bg,#e3f2fd)] text-[var(--color-info,#1565c0)]'
                                    : 'bg-[rgba(0,0,0,.05)] text-[var(--text-color-calendar-week)] opacity-50 hover:opacity-80'
                                }`}>
                                {mod}
                              </span>
                            );
                          })}
                        </div>
                      </StyledTableRow>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <div className='absolute bottom-0 left-0 p-[5px] flex justify-between bg-[var(--bg-top-fix)] w-full items-center rounded-b-[5px] px-[20px]'>
            <Paginations
              rows={companyRow}
              rowsPerPage={rowsPerPage}
              page={page}
              setRowsPerPage={setRowsPerPage}
              setPage={setPage}
              arrayPages={[10, 20, 50]}
            />
            <div className="flex gap-[5px] items-center text-[16px]">
              <div className="user-count-view flex gap-[5px]"><span className='user-count opacity-70'>{companyRow?.length}</span></div>
              <FontAwesomeIcon icon={faBuilding} className='text-[15px] opacity-30 text-[var(--text-color)]' />
            </div>
          </div>
          {openDel && <DeleteArrayAll
            open={openDel}
            setOpen={setOpenDel}
            refresh={refreshCompany}
            selectedArrayId={selected}
            setSelected={setSelected}
            wordMenu={'tariff'}
            pd={'tariff'}
          />}
        </div>
      ) : (
        <EmptyState icon="📋" message={t('No tariff available') as string} />
      )}
    </>
  );
};

export default TariffTable;